import React, { FC } from 'react';
import styled from 'styled-components';
import { Link } from '../buttons';
import { HelpIcon, HelpIconContainer } from '../helpIconContainer';
import Stack from '../stack/stack';
import { ZSwitchVariant, ZSwitch } from '../zswitch';

const SwitchGroupContainer = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: start;
    gap: 4px 0;
    width: auto;
`;

const SwitchGroupLeftWrapper = styled.div`
    display: flex;
    place-self: center;
    width: auto;
    margin-right: 10px;
`;

const ParentContainer = styled.div`
    display: inline-flex;
    flex-direction: row;
`;

const SwitchGroupRightWrapper = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-direction: column;
    gap: 10px;
    margin-right: 5px;
`;

const SwitchGroupLabelContainer = styled.div`
    padding-top: 2px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
`;

const SwitchGroupLabel = styled.span`
    margin: 0;
    font-size: 14px;
    color: #263238;
`;

const SwitchGroupLink = styled(Link)`
    font-size: 14px;
`;

const CaptionContainer = styled.div`
    display: flex;
    grid-column: 2;
`;

const SwitchGroupCaption = styled.span`
    display: inline-block;
    font-size: 12px;
    line-height: 1.4;
    color: #78909c;
`;

const StyledHelpIconContainer = styled(HelpIconContainer)`
    vertical-align: middle;
`;

const BadgeContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;

    > *:first-child {
        margin-left: 24px;
    }
`;

export const SubSwitchGroupContent = styled(Stack)`
    margin-top: 12px;
    margin-left: 46px;
`;

interface SwitchProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    ariaLabel?: string;
    label: string | React.ReactElement<any>;
    caption?: React.ReactNode;
    readMore?: { label: string; link: string };
    className?: string;
    helpToolTip?: React.ReactElement<any> | string | null;
    helpTooltipPosition?: 'left' | 'top' | 'right' | 'bottom';
    iconTooltip?: React.ReactNode;
    disabled?: boolean;
    variant?: ZSwitchVariant;
    badges?: React.ReactNode[];
}

export const _switchGroup: FC<SwitchProps> = ({
    checked,
    onChange,
    ariaLabel,
    readMore,
    className,
    label,
    caption,
    helpToolTip,
    helpTooltipPosition,
    iconTooltip,
    disabled,
    variant,
    badges
}) => {
    return (
        <SwitchGroupContainer className={className}>
            <SwitchGroupLeftWrapper>
                <ZSwitch
                    checked={checked}
                    onChange={onChange}
                    disabled={disabled}
                    variant={variant ?? 'default'}
                    ariaLabel={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
                />
            </SwitchGroupLeftWrapper>
            <ParentContainer>
                <SwitchGroupRightWrapper>
                    <SwitchGroupLabelContainer>{label && <SwitchGroupLabel>{label}</SwitchGroupLabel>}</SwitchGroupLabelContainer>
                    {readMore?.label && (
                        <SwitchGroupLink target={'_blank'} href={readMore.link}>
                            {readMore.label}
                        </SwitchGroupLink>
                    )}
                </SwitchGroupRightWrapper>
                {helpToolTip && (
                    <StyledHelpIconContainer tooltip={helpToolTip} position={helpTooltipPosition}>
                        {iconTooltip || <HelpIcon />}
                    </StyledHelpIconContainer>
                )}
                {badges && badges.length > 0 && (
                    <BadgeContainer>
                        {badges.map((badge, index) => (
                            <React.Fragment key={index}>{badge}</React.Fragment>
                        ))}
                    </BadgeContainer>
                )}
            </ParentContainer>
            {caption && (
                <CaptionContainer>
                    <SwitchGroupCaption>{caption}</SwitchGroupCaption>
                </CaptionContainer>
            )}
        </SwitchGroupContainer>
    );
};

export const SwitchGroup = styled(_switchGroup)``;
